import React, { useState, useMemo } from 'react'
import type { Block, Variable } from '../types'
import { BLOCKS_WITH_ACCENT } from '../types'
import { Trash2, Copy, RotateCcw, Palette, Columns3, Paintbrush, ChevronDown } from 'lucide-react'
import AnimationEditor from './AnimationEditor'
import BackgroundEditor from './BackgroundEditor'
import VisibilityEditor from './properties/VisibilityEditor'

const BLOCK_ACCENT_PRESETS = [
  { name: 'Default', value: '' },
  { name: 'Indigo', value: '#5048e5' },
  { name: 'Electric Blue', value: '#2563eb' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Fuchsia', value: '#d946ef' },
  { name: 'Neon Green', value: '#22d3ee' },
  { name: 'Slate', value: '#0f172a' },
]
import TextProperties from './properties/TextProperties'
import SliderProperties from './properties/SliderProperties'
import ChartProperties from './properties/ChartProperties'
import ButtonProperties from './properties/ButtonProperties'
import ImageProperties from './properties/ImageProperties'
import StepsProperties from './properties/StepsProperties'
import ChatBubbleProperties from './properties/ChatBubbleProperties'
import CodeBlockProperties from './properties/CodeBlockProperties'
import ToggleProperties from './properties/ToggleProperties'
import DropdownProperties from './properties/DropdownProperties'
import DividerProperties from './properties/DividerProperties'
import VideoProperties from './properties/VideoProperties'
import TabsProperties from './properties/TabsProperties'
import NumberInputProperties from './properties/NumberInputProperties'
import CompareProperties from './properties/CompareProperties'
import PollProperties from './properties/PollProperties'
import QuizProperties from './properties/QuizProperties'

interface PropertiesPanelProps {
  block: Block | null
  blocks: Block[]
  variables: Variable[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
  onRemoveBlock: (id: string) => void
  onDuplicateBlock: (id: string) => void
  onResetBlock: (id: string) => void
  onUpdateVariable: (name: string, updates: Partial<Variable>) => void
  onRenameVariable: (oldName: string, newName: string) => void
  onAddVariable: (name?: string) => string
  linkedBlocks: Block[]
}

function BlockBackgroundSection({ block, onUpdateBlock }: { block: Block; onUpdateBlock: (id: string, updates: Partial<Block>) => void }) {
  const [open, setOpen] = useState(!!block.background)

  return (
    <div className="pt-4 border-t border-slate-100 space-y-2">
      <div className="flex items-center justify-between">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 group"
        >
          <Paintbrush className="h-3.5 w-3.5 text-slate-400" />
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest cursor-pointer">
            Background
          </label>
          {block.background && (
            <div
              className="size-3 rounded-full border border-slate-200 shrink-0"
              style={
                block.background.type === 'solid'
                  ? { backgroundColor: block.background.value }
                  : block.background.type === 'gradient'
                  ? { background: block.background.value }
                  : { backgroundColor: '#5048e5' }
              }
            />
          )}
          <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {block.background && (
          <button
            onClick={() => onUpdateBlock(block.id, { background: undefined })}
            className="text-[10px] font-medium text-slate-400 hover:text-red-500 transition-colors"
          >
            Clear
          </button>
        )}
      </div>
      {open && (
        <BackgroundEditor
          background={block.background}
          onSetBackground={(bg) => onUpdateBlock(block.id, { background: bg })}
        />
      )}
    </div>
  )
}

export default function PropertiesPanel({
  block,
  blocks,
  variables,
  onUpdateBlock,
  onRemoveBlock,
  onDuplicateBlock,
  onResetBlock,
  onUpdateVariable,
  onRenameVariable,
  onAddVariable,
  linkedBlocks,
}: PropertiesPanelProps) {
  // Extend variables with poll/quiz auto-exposed derived variables
  const extendedVariables = useMemo(() => {
    const extra: Variable[] = []
    for (const b of blocks) {
      if (b.type === 'poll' && b.variable) {
        const config = (b.config || {}) as Record<string, unknown>
        const options = (config.options as { label: string; value: string }[]) || []
        extra.push({ name: `${b.variable}_total`, type: 'slider', default: 0 })
        for (const opt of options) {
          extra.push({ name: `${b.variable}_${opt.value}`, type: 'slider', default: 0 })
        }
      }
    }
    return extra.length > 0 ? [...variables, ...extra] : variables
  }, [variables, blocks])

  if (!block) {
    return (
      <aside className="w-80 border-l border-slate-200 bg-white flex flex-col shrink-0">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-900">Properties</h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-slate-500">Select a block to edit its properties.</p>
        </div>
      </aside>
    )
  }

  const variable = block.variable ? variables.find((v) => v.name === block.variable) : null

  return (
    <aside className="w-80 border-l border-slate-200 bg-white flex flex-col shrink-0">
      {/* Header */}
      <div className="p-5 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-900">Properties</h2>
        <div className="flex gap-1">
          <button
            onClick={() => onDuplicateBlock(block.id)}
            className="p-1.5 hover:bg-slate-100 rounded transition-colors"
            title="Duplicate block"
          >
            <Copy className="h-4 w-4 text-slate-400" />
          </button>
          <button
            onClick={() => onRemoveBlock(block.id)}
            className="p-1.5 hover:bg-slate-100 rounded transition-colors"
            title="Delete block"
          >
            <Trash2 className="h-4 w-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* Properties Content */}
      <div className="p-5 overflow-y-auto space-y-6 flex-1">
        {block.type === 'text' && (
          <TextProperties block={block} variables={variables} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'slider' && variable && (
          <SliderProperties
            block={block}
            variable={variable}
            linkedBlocks={linkedBlocks}
            onUpdateBlock={onUpdateBlock}
            onUpdateVariable={onUpdateVariable}
            onRenameVariable={onRenameVariable}
          />
        )}

        {block.type === 'chart' && (
          <ChartProperties block={block} blocks={blocks} variables={variables} onUpdateBlock={onUpdateBlock} onAddVariable={onAddVariable} />
        )}

        {block.type === 'button' && (
          <ButtonProperties block={block} variables={variables} onUpdateBlock={onUpdateBlock} onAddVariable={onAddVariable} />
        )}

        {block.type === 'image' && (
          <ImageProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'steps' && (
          <StepsProperties block={block} blocks={blocks} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'chat_bubble' && (
          <ChatBubbleProperties block={block} variables={variables} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'codeblock' && (
          <CodeBlockProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'toggle' && variable && (
          <ToggleProperties
            block={block}
            variable={variable}
            linkedBlocks={linkedBlocks}
            onUpdateBlock={onUpdateBlock}
            onUpdateVariable={onUpdateVariable}
            onRenameVariable={onRenameVariable}
          />
        )}

        {block.type === 'dropdown' && variable && (
          <DropdownProperties
            block={block}
            variable={variable}
            linkedBlocks={linkedBlocks}
            onUpdateBlock={onUpdateBlock}
            onUpdateVariable={onUpdateVariable}
            onRenameVariable={onRenameVariable}
          />
        )}

        {block.type === 'divider' && (
          <DividerProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'video' && (
          <VideoProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'compare' && (
          <CompareProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'poll' && (
          <PollProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'quiz' && (
          <QuizProperties block={block} onUpdateBlock={onUpdateBlock} />
        )}

        {block.type === 'tabs' && variable && (
          <TabsProperties
            block={block}
            variable={variable}
            linkedBlocks={linkedBlocks}
            onUpdateBlock={onUpdateBlock}
            onUpdateVariable={onUpdateVariable}
            onRenameVariable={onRenameVariable}
          />
        )}

        {block.type === 'number_input' && variable && (
          <NumberInputProperties
            block={block}
            variable={variable}
            linkedBlocks={linkedBlocks}
            onUpdateBlock={onUpdateBlock}
            onUpdateVariable={onUpdateVariable}
            onRenameVariable={onRenameVariable}
          />
        )}

        {/* Visibility — all block types */}
        <div className="pt-4 border-t border-slate-100">
          <VisibilityEditor
            visibility={block.visibility}
            variables={extendedVariables}
            onChange={(vis) => onUpdateBlock(block.id, { visibility: vis })}
          />
        </div>

        {/* Block Width — all block types */}
        <div className="pt-4 border-t border-slate-100 space-y-2">
          <div className="flex items-center gap-1.5">
            <Columns3 className="h-3.5 w-3.5 text-slate-400" />
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
              Width
            </label>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {([
              { value: 3, label: 'Full', visual: [true, true, true] },
              { value: 2, label: '2/3', visual: [true, true, false] },
              { value: 1, label: '1/3', visual: [true, false, false] },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                onClick={() => onUpdateBlock(block.id, { span: opt.value === 3 ? undefined : opt.value })}
                className={`flex flex-col items-center gap-1 py-2 px-1 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition-all ${
                  (block.span || 3) === opt.value
                    ? 'border-[#5048e5] bg-[#5048e5]/5 text-[#5048e5]'
                    : 'border-slate-200 text-slate-400 hover:border-slate-300'
                }`}
              >
                <div className="flex gap-0.5">
                  {opt.visual.map((active, i) => (
                    <div
                      key={i}
                      className={`w-3 h-2 rounded-sm ${
                        active
                          ? (block.span || 3) === opt.value ? 'bg-[#5048e5]' : 'bg-slate-300'
                          : 'bg-slate-100'
                      }`}
                    />
                  ))}
                </div>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Animation — all block types */}
        <div className="pt-4 border-t border-slate-100">
          <AnimationEditor
            animation={block.animation}
            variables={variables}
            onChange={(anim) => onUpdateBlock(block.id, { animation: anim })}
          />
        </div>

        {/* Block Background — all block types (collapsible) */}
        <BlockBackgroundSection
          block={block}
          onUpdateBlock={onUpdateBlock}
        />

        {/* Block Accent Color — only for blocks that use it */}
        {BLOCKS_WITH_ACCENT.has(block.type) && (
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <div className="flex items-center gap-1.5">
              <Palette className="h-3.5 w-3.5 text-slate-400" />
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
                Accent Color
              </label>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {BLOCK_ACCENT_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  title={preset.name}
                  onClick={() => onUpdateBlock(block.id, { accent: preset.value || undefined })}
                  className={`size-6 rounded-full transition-all border-2 ${
                    (block.accent || '') === preset.value
                      ? 'ring-2 ring-offset-1 ring-[#5048e5] scale-110'
                      : 'hover:scale-110'
                  } ${preset.value === '' ? 'border-dashed border-slate-300 bg-white' : 'border-transparent'}`}
                  style={preset.value ? { backgroundColor: preset.value } : undefined}
                >
                  {preset.value === '' && (
                    <span className="text-[8px] text-slate-400 font-bold leading-none flex items-center justify-center h-full">
                      AUTO
                    </span>
                  )}
                </button>
              ))}
            </div>
            {block.accent && (
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={block.accent}
                  onChange={(e) => onUpdateBlock(block.id, { accent: e.target.value })}
                  className="size-6 rounded cursor-pointer border border-slate-200"
                />
                <span className="text-xs font-mono text-slate-400">{block.accent}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reset to Defaults */}
      <div className="p-5 mt-auto border-t border-slate-100">
        <button
          onClick={() => onResetBlock(block.id)}
          className="w-full py-2.5 border border-slate-200 text-slate-600 font-bold text-xs uppercase tracking-widest rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Defaults
        </button>
      </div>
    </aside>
  )
}
